import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/**
 * Editorial button system. Variants restrained to five; the hierarchy matches
 * how the UI uses them — primary actions, secondary paths, quiet actions, link-
 * alikes, and a destructive variant that only shows for AHPRA/withdraw/delete.
 *
 * Coral accent is reserved for urgency states only (see doc 24 §2.1) — there is
 * no `accent` button variant by design.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-pb-primary focus-visible:ring-offset-2 focus-visible:ring-offset-pb-bg disabled:pointer-events-none disabled:opacity-50 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        primary:
          "bg-pb-primary text-pb-on-primary shadow-sm hover:bg-pb-primary-600 active:translate-y-px",
        outline:
          "border border-pb-border-strong bg-pb-surface text-pb-ink hover:bg-pb-surface-2 active:translate-y-px",
        ghost: "text-pb-ink hover:bg-pb-surface-2",
        link: "text-pb-primary underline-offset-4 hover:underline",
        danger:
          "bg-pb-danger text-white shadow-sm hover:bg-pb-danger/90 active:translate-y-px",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4",
        lg: "h-12 px-6 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
